import type { Renewal, RenewalHistoryEntry } from '@bekebi/contracts/api';
import {
  RENEWAL_CONDITION_LABELS,
  formatDate,
  formatDateTime,
  renewalStatusText,
} from '../services/format';

/**
 * Renewal presentation. Dates are calendar dates (YYYY-MM-DD) shown without any timezone shift;
 * the condition and countdown are the API's; the price is the API's decimal string, never parsed.
 */

export function formatMoney(amount: string, currency: string): string {
  return `${currency} ${amount}`;
}

const CONDITION_STYLE: Record<
  NonNullable<Renewal['condition']>,
  { symbol: string; className: string }
> = {
  upcoming: { symbol: '○', className: 'border-line-strong bg-surface text-fg-2' },
  due_soon: { symbol: '!', className: 'border-warning-line bg-warning-soft text-warning' },
  due_today: { symbol: '!', className: 'border-warning-line bg-warning-soft text-warning' },
  overdue: { symbol: '✕', className: 'border-danger-line bg-danger-soft text-danger' },
};

/** Open terms: the API's condition with its countdown label. Closed terms: "Closed". */
export function RenewalStateBadge({ renewal }: { renewal: Renewal }) {
  if (renewal.state === 'closed' || !renewal.condition) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-raised px-2.5 py-0.5 text-xs font-semibold text-fg-2">
        <span aria-hidden="true">■</span>
        Closed
      </span>
    );
  }
  const style = CONDITION_STYLE[renewal.condition];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${style.className}`}
    >
      <span aria-hidden="true">{style.symbol}</span>
      {renewal.countdown
        ? renewalStatusText(renewal.condition, renewal.countdown)
        : RENEWAL_CONDITION_LABELS[renewal.condition]}
    </span>
  );
}

const ACTOR_LABELS: Record<RenewalHistoryEntry['actorType'], string> = {
  admin: 'Admin',
  operations: 'ICT',
  system: 'System',
  integration: 'Integration',
};

const FIELD_LABELS: Record<string, string> = {
  termStart: 'Term start',
  renewalDate: 'Renewal date',
  price: 'Price',
  state: 'State',
  closedAt: 'Closed at',
};

function fieldValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  const text = String(value);
  if ((key === 'termStart' || key === 'renewalDate') && /^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return formatDate(text);
  }
  if (key === 'closedAt') return formatDateTime(text);
  if (key === 'price') return formatMoney(text, 'USD');
  if (key === 'state') return text === 'open' ? 'Open' : text === 'closed' ? 'Closed' : text;
  return text;
}

const record = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** One history entry, described from its recorded changes. Unknown fields are not shown. */
export function HistoryEntry({ entry }: { entry: RenewalHistoryEntry }) {
  const c = entry.changes;
  let title: string;
  let lines: string[] = [];
  if (entry.event === 'term_opened') {
    title = 'Term opened';
    lines = ['termStart', 'renewalDate', 'price']
      .filter((k) => k in c)
      .map((k) => `${FIELD_LABELS[k]}: ${fieldValue(k, c[k])}`);
    if (typeof c.renews === 'string') lines.push('Opened by renewing the previous term.');
  } else if (entry.event === 'term_updated') {
    title = 'Term changed';
    const before = record(c.before);
    const after = record(c.after);
    lines = Object.keys(after)
      .filter((k) => k in FIELD_LABELS)
      .map((k) => `${FIELD_LABELS[k]}: ${fieldValue(k, before[k])} → ${fieldValue(k, after[k])}`);
  } else {
    title = 'Term closed';
    const after = record(c.after);
    if (after.closedAt) lines.push(`Closed at: ${fieldValue('closedAt', after.closedAt)}`);
    if (typeof c.renewedBy === 'string') lines.push('Closed by renewing into a new term.');
  }
  return (
    <div className="text-sm">
      <p className="font-semibold text-fg">
        {title}{' '}
        <span className="font-normal text-muted">
          · {formatDateTime(entry.occurredAt)} · {ACTOR_LABELS[entry.actorType]}
        </span>
      </p>
      {lines.length > 0 ? (
        <ul className="mt-1 text-fg-2">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
